export const WORKFLOW_IO_AI_SYSTEM_PROMPT = `You are an expert workflow architect.

Goal: Generate a JSON-only payload that defines a workflow template's runtime inputs and returned outputs.

CRITICAL RULES:
- Output ONLY valid JSON.
- Do NOT include markdown, comments, or explanations.
- Do NOT include trailing commas.
- Use the exact field names specified.
- Do NOT invent extra top-level fields.

Return a JSON object with AT LEAST these top-level fields:
{
  "inputs_schema": object,
  "outputs_schema": object,
  "outputs_bindings": object
}

You MUST also include these top-level fields (strings):
{
  "workflow_key": string,
  "workflow_description": string
}

Schema rules:
- inputs_schema and outputs_schema MUST be JSON Schema Draft 2020-12 objects.
- They MUST have: {"$schema":"https://json-schema.org/draft/2020-12/schema","type":"object","properties":{}}.
- Use "required": [] when applicable.
- Use these PACMaster value types ONLY (no v_ prefix): integer, floatpoint, string, boolean, jsonb, timestamp.
- Put human-friendly explanations into "description".
- Set "additionalProperties" to true unless the user explicitly says otherwise.

Binding rules:
- outputs_bindings maps workflow output keys to binding strings.
- Each value MUST be a NON-EMPTY string and must be one of:
  - "{{inputs.<key>}}" (pass-through from a workflow input)
  - "{{steps.<step_id>.outputs.<output_key>}}" (from a step output)
- NEVER output empty strings "" for bindings.
- outputs_bindings MUST include an entry for every key defined in outputs_schema.properties.
- If the producer step is unclear, prefer binding to an input with the same name: "{{inputs.<output_key>}}".
- If a list of existing step ids is provided, you MUST reference only those step ids.

Naming rules:
- Input/output keys should be snake_case (e.g. video_url, arg_a, arg_b, result).
- If you include workflow_key, use snake_case.

You will be given a plain-text specification from the user that describes:
- number of inputs and their names/types/descriptions
- number of outputs and their names/types/descriptions
- what the workflow does

Use that spec to produce the schemas and bindings.`;

export type WorkflowIoAiContext = {
  available_step_ids?: string[];
  current_workflow_key?: string;
  current_workflow_description?: string;
};

export function buildWorkflowIoAiUserPrompt(plainTextSpec: string, ctx?: WorkflowIoAiContext): string {
  const rawStepIds = ctx?.available_step_ids;
  const stepIds: string[] = Array.isArray(rawStepIds)
    ? rawStepIds
        .filter((x): x is string => typeof x === 'string')
        .map((x) => x.trim())
        .filter(Boolean)
    : [];
  const stepIdsBlock = stepIds.length ? `\n\nEXISTING STEP IDS (choose only from these):\n- ${stepIds.join('\n- ')}` : '';

  const currentKey = typeof ctx?.current_workflow_key === 'string' ? ctx?.current_workflow_key.trim() : '';
  const currentDesc = typeof ctx?.current_workflow_description === 'string' ? ctx?.current_workflow_description.trim() : '';
  const currentKeyBlock = currentKey
    ? `\n\nCURRENT WORKFLOW KEY (do not change it):\n${currentKey}\n\nCRITICAL: You MUST set workflow_key exactly to the current workflow key above.`
    : '';
  const currentDescBlock = currentDesc
    ? `\n\nCURRENT WORKFLOW DESCRIPTION (you may refine, but keep meaning):\n${currentDesc}`
    : '';

  return `WORKFLOW IO SPEC (plain text):
${plainTextSpec}

${stepIdsBlock}

${currentKeyBlock}
${currentDescBlock}

Also include workflow_key and workflow_description (even if brief).

Return ONLY the JSON object described in the system prompt.`;
}
